import { BookmarkSubCategory } from '@softapps/core';
import { MasterdataCustomer } from '../types/masterdata-entities';
import { MasterdataBadge } from '../types/masterdata-badge';
import { defineMasterdataEntity } from '../types/masterdata-entity-definition';

/** Full customer name (salutation + name1 + name2 + name3), trimmed. */
function customerName(c: MasterdataCustomer): string {
  return [c.salutation, c.name1, c.name2, c.name3].filter(Boolean).join(' ').trim();
}

/** Customer number with optional subnumber (`123` or `123/4`). */
function customerNumberLabel(c: MasterdataCustomer): string {
  return c.customerSubnumber > 0
    ? `${c.customerNumber}/${c.customerSubnumber}`
    : `${c.customerNumber ?? ''}`;
}

/** Barverkauf (one-time/walk-in collector account) badge; a normal customer
 *  renders no badge. */
export function oneTimeCustomerBadge(c: MasterdataCustomer): MasterdataBadge | null {
  return c.oneTimeCustomer ? { label: 'Barverkauf', tone: 'warning' } : null;
}

/**
 * Customer (Kunde) masterdata record. `displayWith` renders `<number> - <name>`;
 * the default filter restricts to main customers (subnumber 0), active, non-blocked.
 */
export const MASTERDATA_CUSTOMER = defineMasterdataEntity<MasterdataCustomer>({
  name: 'customer',
  responseKey: 'customers',
  defaultFilter: [
    { type: 'numberEquals', name: 'customerSubnumber', value: 0 },
    { type: 'booleanIsTrue', name: 'active' },
    { type: 'booleanIsFalse', name: 'blocked' },
  ],
  displayWith: (c) => {
    const name = customerName(c);
    const number = customerNumberLabel(c);
    return name ? `${number} - ${name}` : number;
  },
  secondaryWith: (c) => [c.postCode, c.city].filter(Boolean).join(' ') || null,
  badgeWith: oneTimeCustomerBadge,
  trackWith: (c) => c.id ?? c.customerNumber ?? '',
  bookmarkSubCategory: BookmarkSubCategory.Customer,
  icon: 'fa-regular fa-user',
  title: 'Kundensuche',
  label: 'Kunde',
  placeholder: 'Kunde suchen...',
  detailSidebar: true,
  columns: [
    {
      label: 'Kunde',
      type: 'entity',
      icon: 'fa-regular fa-user',
      value: (c) => customerName(c),
      secondary: (c) => [c.postCode, c.city].filter(Boolean).join(' ') || null,
    },
    { label: 'Nr.', value: (c) => customerNumberLabel(c) },

    { label: 'Straße', value: (c) => c.street },
    { label: 'Hausnummer', value: (c) => c.streetNumber },
    { label: 'PLZ', value: (c) => c.postCode },
    { label: 'Ort', value: (c) => c.city },
    { label: 'Land', value: (c) => c.countryCode },
    { label: 'Telefon', value: (c) => c.phone },
    { label: 'E-Mail', value: (c) => c.mail1 },

    { label: 'Kurzbezeichnung', value: (c) => c.shortName },
    { label: 'Barverkauf', type: 'badge', badge: oneTimeCustomerBadge },
    { label: 'Kategorie', value: (c) => c.category },
    { label: 'Kundengruppe', value: (c) => c.customerGroup },
    { label: 'Kundengruppe (Bez.)', value: (c) => c.customerGroupName },
  ],
});
